import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { formatDateLocal, getStartOfWeek, addDays, getWeekNumber } from '../utils/dateUtils';

// --- TYPES ---

export type CompanyType = 'oger' | 'wag';

export interface Employee {
  id: string;
  full_name: string;
  role?: string;
  company?: CompanyType;
}

export interface Project {
  id: string;
  code: string;
  name: string;
  color?: string;
  category?: string;
  parent_entity?: 'JLO' | 'GP' | 'WAG' | null;
}

export interface DiversSubProject {
  id: string;
  parent_project_id: string;
  divers_number: string;
  name: string;
  address?: string | null;
  zone?: string | number | null;
  is_active: boolean;
}

export type Period = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'AM' | 'PM' | 'DAY';

export interface Assignment {
  id?: string;
  employee_id: string;
  project_id: string;
  assignment_date: string;
  period: Period;
  divers_sub_project_id?: string | null;
}

export interface Leave {
  employee_id: string;
  start_date: string;
  end_date: string;
  type: string;
}

export interface TrainingDay {
  employee_id: string;
  day: string;
  description: string;
}

export interface PlanningValidation {
  status: 'draft' | 'published';
  created_at: string;
}

export interface PDFRowData {
  name: string;
  [dateKey: string]: string;
}

// --- UTILITAIRES ---

export const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  // Teinte issue du hash, saturation/luminosité fixes : texte toujours sombre
  // et lisible sur le fond pastel de la case (le hash brut donnait des néons).
  const h = Math.abs(hash) % 360;
  const s = 0.7;
  const l = 0.3;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const LEAVE_LABELS: Record<string, string> = {
  cp: 'CP', rtt: 'RTT', maladie: 'MALADIE', sans_solde: 'SANS SOLDE', formation: 'FORMATION', autre: 'AUTRE', training: 'FORMATION'
};

export const WAG_COLORS = {
  bg: '#ecfdf5',
  bgGradient: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
  border: '#10b981',
  text: '#047857',
  badge: '#d1fae5'
};

export { SIEGE_COORDS, getEntityColors, calculateDistance, determineZone } from '../utils/geoUtils';

export interface DiversFormData {
  divers_number: string;
  name: string;
  address: string;
  lat?: number;
  lng?: number;
  zone?: string | number;
  parent_project_id: string;
}

// --- HOOK ---

export function useSchedulerData() {
  const { toast } = useToast();
  // UI state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  // Data state
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [rowEmployees, setRowEmployees] = useState<Employee[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [trainingDays, setTrainingDays] = useState<TrainingDay[]>([]);
  const [validationInfo, setValidationInfo] = useState<PlanningValidation | null>(null);
  const [diversSubProjects, setDiversSubProjects] = useState<DiversSubProject[]>([]);
  const [diversGroups, setDiversGroups] = useState<Project[]>([]);

  // Date memos
  const startOfWeek = useMemo(() => getStartOfWeek(currentDate), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }).map((_, i) => addDays(startOfWeek, i)), [startOfWeek]);
  const currentWeekNumber = getWeekNumber(startOfWeek);
  const currentYear = startOfWeek.getFullYear();

  // Derived
  const isPublished = validationInfo?.status === 'published';

  // Fetch
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startStr = formatDateLocal(weekDays[0]);
      const endStr = formatDateLocal(weekDays[6]);

      const [empsRes, projsRes, assignsRes, leavesRes, trainingRes, validRes, subProjsRes] = await Promise.all([
        supabase.from('employees').select('id, full_name, role, company').eq('is_active', true).not('role', 'in', '(secretariat,admin)').order('full_name'),
        supabase.from('projects').select('id, code, name, category, parent_entity').eq('is_active', true).order('code'),
        supabase.from('project_assignments').select('*').gte('assignment_date', startStr).lte('assignment_date', endStr),
        supabase.from('leaves').select('employee_id, start_date, end_date, type').eq('status', 'validated').or(`start_date.lte.${endStr},end_date.gte.${startStr}`),
        supabase.from('training_days').select('employee_id, day, description').gte('day', startStr).lte('day', endStr),
        supabase.from('planning_validations').select('status, created_at').eq('year', currentYear).eq('week_number', currentWeekNumber).maybeSingle(),
        supabase.from('divers_sub_projects').select('*').eq('is_active', true)
      ]);

      if (empsRes.error) throw empsRes.error;

      const emps = empsRes.data || [];
      setEmployees(emps);

      // Charger l'ordre sauvegardé pour cette semaine spécifique
      const weekKey = `employee_display_order_${currentYear}_S${currentWeekNumber}`;
      const savedOrder = localStorage.getItem(weekKey);
      if (savedOrder) {
        try {
          const orderIds: string[] = JSON.parse(savedOrder);
          const sortedEmps = [...emps].sort((a, b) => {
            const indexA = orderIds.indexOf(a.id);
            const indexB = orderIds.indexOf(b.id);
            if (indexA === -1 && indexB === -1) return a.full_name.localeCompare(b.full_name);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
          });
          setRowEmployees(sortedEmps);
        } catch {
          setRowEmployees(emps);
        }
      } else {
        setRowEmployees(emps);
      }

      const allProjects = (projsRes.data || []).map(p => ({ ...p, color: stringToColor(p.id) }));
      setProjects(allProjects);
      setDiversGroups(allProjects.filter(p => p.category === 'divers' && p.code !== 'SYS-FORM'));
      setAssignments((assignsRes.data || []) as Assignment[]);
      setLeaves((leavesRes.data || []) as Leave[]);
      setTrainingDays((trainingRes.data || []) as TrainingDay[]);
      setDiversSubProjects((subProjsRes.data || []) as DiversSubProject[]);

      const validationData = validRes.data as { status?: string, created_at: string } | null;
      if (validationData) {
        setValidationInfo({
          status: (validationData.status === 'published' ? 'published' : 'draft'),
          created_at: validationData.created_at
        });
      } else {
        setValidationInfo(null);
      }

    } catch (error) {
      console.error("Erreur de chargement:", error);
      toast.error("Impossible de charger le planning. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
  }, [weekDays, currentYear, currentWeekNumber]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return {
    // UI state
    currentDate, setCurrentDate,
    loading,
    // Data state
    employees, setEmployees,
    rowEmployees, setRowEmployees,
    projects,
    assignments, setAssignments,
    leaves,
    trainingDays,
    validationInfo, setValidationInfo,
    diversSubProjects, setDiversSubProjects,
    diversGroups,
    // Computed
    startOfWeek,
    weekDays,
    currentWeekNumber,
    currentYear,
    isPublished,
    // Actions
    fetchData,
  };
}
